import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Alert,
  LinearProgress,
  Fab,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { PageLayout } from '../components/ui/PageLayout';
import {
  Add as AddIcon,
  CloudSync as CloudSyncIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Folder as FolderIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  useFolderSyncs,
  useDeleteFolderSync,
  useSyncFolderSync,
  useToggleFolderSyncStatus,
  type FolderSync,
} from '../hooks/api/useFolderSyncs';
import { ShareFolderDialog } from '../components/dialogs';
import { log } from '../utils/errors/logger';
import { formatDateTime } from '../utils/formatting/formatters';

const SharedFolders: React.FC = () => {
  const { t, i18n } = useTranslation();
  
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    sync?: FolderSync;
  }>({ open: false });
  
  const [shareFolderDialog, setShareFolderDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    sync?: FolderSync;
  }>({ open: false, mode: 'create' });

  // API hooks
  const { data: folderSyncs, isLoading, error, isError } = useFolderSyncs();
  const deleteMutation = useDeleteFolderSync();
  const syncMutation = useSyncFolderSync();
  const statusMutation = useToggleFolderSyncStatus();

  // Debug logging
  React.useEffect(() => {
    log.debug('SharedFolders query state', { 
      folderSyncs, 
      isLoading, 
      error: error?.message || error, 
      isError,
      hasData: Boolean(folderSyncs),
      dataLength: folderSyncs?.length 
    });
  }, [folderSyncs, isLoading, error, isError]);

  const getProviderIcon = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'sharepoint':
        return '📊';
      case 'nextcloud':
        return '☁️';
      case 'googledrive':
        return '📚';
      case 'dropbox':
        return '📦';
      default:
        return <FolderIcon />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'success';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'paused':
        return <PauseIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircleIcon />;
    }
  };

  const handleEdit = (sync: FolderSync) => {
    setShareFolderDialog({ open: true, mode: 'edit', sync });
  };

  const handleDelete = (sync: FolderSync) => {
    setDeleteDialog({ open: true, sync });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.sync) {
      deleteMutation.mutate(deleteDialog.sync.id, {
        onSuccess: () => {
          setDeleteDialog({ open: false });
        },
      });
    }
  };

  const handleSync = (sync: FolderSync) => {
    syncMutation.mutate(sync.id);
  };

  const handleToggleStatus = (sync: FolderSync) => {
    const newStatus = sync.status === 'active' ? 'paused' : 'active';
    statusMutation.mutate({ id: sync.id, status: newStatus });
  };

  if (isLoading) {
    return (
      <PageLayout
        title={t('sharedFolders.title')}
        isLoading={true}
        maxWidth="lg"
      >
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('sharedFolders.loading')}
        </Typography>
      </PageLayout>
    );
  }

  if (error) {
    log.error('Failed to load shared folders', { error });
    return (
      <PageLayout
        title={t('sharedFolders.title')}
        isLoading={false}
        maxWidth="lg"
      >
        <Alert severity="error">
          {t('sharedFolders.loadError')}: {error?.message || 'Unknown error'}
        </Alert>
      </PageLayout>
    );
  }

  return (
    <>
      <PageLayout
        title={t('sharedFolders.title')}
        isLoading={false}
        maxWidth="lg"
        headerActions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShareFolderDialog({ open: true, mode: 'create' })}
          >
            {t('sharedFolders.addFolder')}
          </Button>
        }
        headerContent={
          <Typography variant="body2" color="text.secondary">
            {t('sharedFolders.subtitle')}
          </Typography>
        }
      >

        {/* Folder Syncs - Compact Cards */}
        {!folderSyncs || folderSyncs.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <CloudSyncIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('sharedFolders.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t('sharedFolders.empty.message')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShareFolderDialog({ open: true, mode: 'create' })}
            >
              {t('sharedFolders.empty.addFirst')}
            </Button>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {folderSyncs.map((sync) => (
              <Card key={sync.id} variant="outlined">
                <CardContent sx={{ pb: 1 }}>
                  {/* Header: Provider, Name and Status */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="h6" component="span">
                          {getProviderIcon(sync.provider)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {sync.provider}
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 500 }} noWrap>
                        {sync.name}
                      </Typography>
                      {sync.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {sync.description}
                        </Typography>
                      )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} sx={{ ml: 2 }}>
                      {getStatusIcon(sync.status)}
                      <Chip
                        label={t(`sharedFolders.status.${sync.status}`)}
                        color={getStatusColor(sync.status)}
                        size="small"
                      />
                    </Box>
                  </Box>

                  {/* Stats and Last Sync Row */}
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                      <Box display="flex" gap={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('sharedFolders.stats.files')}
                          </Typography>
                          <Typography variant="h6" color="primary" sx={{ lineHeight: 1 }}>
                            {sync.sync_stats.total_files_in_source || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('sharedFolders.table.lastSync')}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <ScheduleIcon fontSize="small" sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {sync.last_sync_at
                                ? formatDateTime(sync.last_sync_at, i18n.language)
                                : t('sharedFolders.neverSynced')
                              }
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Box display="flex" gap={0.5} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                        <Tooltip title={t('sharedFolders.actions.sync')}>
                          <IconButton
                            size="small"
                            onClick={() => handleSync(sync)}
                            disabled={syncMutation.isPending}
                            color="primary"
                          >
                            <SyncIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={sync.status === 'active'
                          ? t('sharedFolders.actions.pause')
                          : t('sharedFolders.actions.resume')
                        }>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(sync)}
                            disabled={statusMutation.isPending}
                          >
                            {sync.status === 'active' ?
                              <PauseIcon fontSize="small" /> :
                              <PlayIcon fontSize="small" />
                            }
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('sharedFolders.actions.edit')}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(sync)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('sharedFolders.actions.delete')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(sync)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Error Message */}
                  {sync.sync_stats.last_error && (
                    <Alert severity="error" size="small" sx={{ mt: 2, py: 0.5 }}>
                      <Typography variant="caption">
                        {sync.sync_stats.last_error}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </PageLayout>


      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
      >
        <DialogTitle>
          {t('sharedFolders.delete.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('sharedFolders.delete.message',
              { name: deleteDialog.sync?.name }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Folder Dialog */}
      <ShareFolderDialog
        open={shareFolderDialog.open}
        onClose={() => setShareFolderDialog({ open: false, mode: 'create' })}
        mode={shareFolderDialog.mode}
        initialData={shareFolderDialog.sync}
        onSuccess={() => {
          // Refresh the list - queries will automatically invalidate
          log.user.action('folder_sync_dialog_success', 'SharedFolders');
        }}
      />

      {/* Floating Add Button (mobile-friendly) */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: { xs: 'flex', md: 'none' } // Only show on mobile
        }}
        onClick={() => setShareFolderDialog({ open: true, mode: 'create' })}
      >
        <AddIcon />
      </Fab>
    </>
  );
};

export default SharedFolders;